import React, { useRef, useLayoutEffect } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { Link } from 'react-router-dom';
import { CheckCircle2, ArrowUpRight } from 'lucide-react';

gsap.registerPlugin(ScrollTrigger);

const highlightItems = [
  { 
    title: "Skilled & Experienced Team", 
    icon: "/images/treat_surgery.png", 
    path: "/about",
    accent: "from-amber-400 to-orange-500",
    bg: "bg-amber-50",
    num: "01"
  },
  { 
    title: "Modern Medical Equipment", 
    icon: "/images/treat_diagnostics.png", 
    path: "/diagnostic-services",
    accent: "from-blue-400 to-indigo-500",
    bg: "bg-blue-50",
    num: "02"
  },
  { 
    title: "Professional Emergency Support", 
    icon: "/images/high_critical.png", 
    path: "/critical-care",
    accent: "from-rose-400 to-red-500",
    bg: "bg-rose-50",
    num: "03"
  },
  { 
    title: "Affordable & Transparent Services", 
    icon: "/images/high_grooming.png", 
    path: "/services",
    accent: "from-violet-400 to-purple-500",
    bg: "bg-violet-50",
    num: "04"
  },
  { 
    title: "Gentle & Caring Approach", 
    icon: "/images/treat_internal.png", 
    path: "/about",
    accent: "from-cyan-400 to-blue-500",
    bg: "bg-cyan-50",
    num: "05"
  }
];

const Highlights = () => {
  const containerRef = useRef(null);

  useLayoutEffect(() => {
    const ctx = gsap.context(() => {
      gsap.fromTo('.high-header',
        { y: 30, opacity: 0 },
        { y: 0, opacity: 1, stagger: 0.1, duration: 1, ease: 'power3.out',
          scrollTrigger: { trigger: '.high-header', start: 'top 90%' }
        }
      );

      gsap.fromTo('.high-card',
        { y: 50, opacity: 0, scale: 0.95 },
        { y: 0, opacity: 1, scale: 1, stagger: 0.08, duration: 0.8, ease: 'back.out(1.4)',
          scrollTrigger: { trigger: '.high-grid', start: 'top 85%' }
        }
      );
    }, containerRef);

    return () => ctx.revert();
  }, []);

  return (
    <section ref={containerRef} className="py-24 md:py-32 bg-white relative overflow-hidden" id="highlights">
      
      {/* Decorative logic */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-screen-2xl h-full pointer-events-none opacity-[0.03]"
           style={{ backgroundImage: 'radial-gradient(circle at 2px 2px, #038903 1px, transparent 0)', backgroundSize: '40px 40px' }} />

      <div className="section-container relative z-10">
        
        {/* ── Header Area ── */}
        <div className="max-w-4xl mx-auto text-center space-y-6 mb-16 md:mb-24">
          <div className="high-header inline-flex items-center gap-4 px-4 py-2 rounded-full bg-primary/5 ring-1 ring-primary/10">
            <CheckCircle2 size={14} className="text-primary" />
            <span className="text-primary font-bold tracking-[0.2em] uppercase text-[10px] md:text-[11px]">
              Our Clinical Excellence
            </span>
          </div>
          
          <h2 className="high-header text-navy font-bold leading-[1.1] tracking-tight text-[clamp(2.5rem,6vw,4.5rem)]">
            Why Pet Parents Choose <br /> 
            <span className="text-primary italic font-normal">Dr. Suresh Pet Care.</span>
          </h2>
          
          <p className="high-header text-muted-foreground text-lg max-w-2xl mx-auto">
            Experience reliable veterinary care with modern treatments and a gentle, caring approach for your pets.
          </p>
        </div>

        {/* ── Grid Layout ── */}
        <div className="high-grid grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-6">
          {highlightItems.map((item, index) => (
            <Link 
              to={item.path}
              key={index} 
              className="high-card group relative bg-slate-50/50 backdrop-blur-sm border border-navy/5 rounded-[2rem] p-8 flex flex-col items-center text-center gap-6 hover:bg-white hover:shadow-[0_20px_50px_rgba(0,0,0,0.08)] hover:-translate-y-2 transition-all duration-500 cursor-pointer overflow-hidden"
            >
              {/* Background gradient orb */}
              <div className={`absolute -top-12 -right-12 w-32 h-32 bg-gradient-to-br ${item.accent} opacity-0 group-hover:opacity-10 blur-2xl transition-opacity duration-700`} />
              
              {/* Icon Container */}
              <div className={`relative z-10 w-20 h-20 rounded-2xl ${item.bg} flex items-center justify-center ring-1 ring-navy/5 shadow-sm group-hover:scale-110 group-hover:-rotate-3 transition-transform duration-500 p-4`}>
                <img 
                  src={item.icon} 
                  alt={item.title} 
                  className="w-full h-full object-contain" 
                />
              </div>

              {/* Text content */}
              <div className="relative z-10 space-y-1 mt-2">
                <span className="text-[10px] font-black uppercase tracking-[0.2em] text-primary/40 group-hover:text-primary transition-colors duration-500">
                  Reliability {item.num}
                </span>
                <h3 className="font-bold text-navy text-lg leading-tight group-hover:text-primary transition-colors duration-500 line-clamp-1">
                  {item.title}
                </h3>
              </div>

              <div className="mt-auto w-10 h-10 rounded-full border border-navy/10 flex items-center justify-center text-navy/40 group-hover:bg-primary group-hover:border-primary group-hover:text-white group-hover:scale-110 transition-all duration-500 shadow-sm mt-4">
                <ArrowUpRight size={18} />
              </div>

              {/* Subtle number background */}
              <span className="absolute bottom-4 right-6 text-[40px] font-black text-navy/[0.03] group-hover:text-navy/[0.06] transition-colors duration-500 scale-150 pointer-events-none tabular-nums">
                {item.num}
              </span>
            </Link>
          ))}

          {/* Special Emergency Card / Contact Teaser */}
          <div className="high-card flex relative bg-primary rounded-[2.5rem] p-8 flex-col items-start justify-center text-left gap-4 overflow-hidden group shadow-xl">
            <div className="absolute top-0 right-0 w-full h-full bg-gradient-to-br from-white/20 to-transparent opacity-30" />
            <h4 className="text-white font-bold text-2xl relative z-10 leading-tight">
              Emergency <br /> Needs?
            </h4>
            <p className="text-white/80 text-xs relative z-10 leading-relaxed font-medium">
              Our critical care unit is available 24/7 for immediate response.
            </p>
            <a href="tel:+919848954921" className="relative z-10 flex items-center gap-2 text-white font-black text-sm uppercase tracking-widest hover:translate-x-1 transition-all">
              Call Now <ArrowUpRight size={16} />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Highlights;
